import { useEffect, useMemo, useState } from 'react'
import { ExternalLink, X } from 'lucide-react'
import { auditProvenanceRecord, fetchLiveOddsHistory, fetchLiveOddsProvenance } from '../lib/liveOddsVnextEvidence.js'
import { resolveBookIdentity } from '../lib/bookLogoRegistry.js'
import { buildOfferDeepLink, proofPageForBook } from '../lib/eventIntelligence.js'
import LiveOddsVNextHistoryChart, { buildHistoryKey } from './LiveOddsVNextHistoryChart.jsx'

function formatOdds(value, format) {
  const number = Number(value)
  if (!Number.isFinite(number)) return 'unpriced'
  if (format === 'american') return number > 0 ? `+${number}` : String(number)
  if (number >= 100) return (1 + number / 100).toFixed(2)
  if (number <= -100) return (1 + 100 / Math.abs(number)).toFixed(2)
  return `raw ${value}`
}

function JsonBlock({ label, value }) {
  return (
    <details className="lovx-json" open={label === 'Raw offer row'}>
      <summary>{label}</summary>
      <pre>{JSON.stringify(value, null, 2)}</pre>
    </details>
  )
}

function EvidenceState({ loading, error, children, emptyLabel }) {
  if (loading) return <div className="lovx-evidence-state">Loading on demand…</div>
  if (error) return <div className="lovx-evidence-state warn">Unavailable: {error}</div>
  return children || <div className="lovx-evidence-state">{emptyLabel}</div>
}

export default function LiveOddsVNextOfferDrawer({ row, categorySlug, oddsFormat, onClose }) {
  const [provenance, setProvenance] = useState(null)
  const [history, setHistory] = useState(null)
  const [provenanceError, setProvenanceError] = useState(null)
  const [historyError, setHistoryError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [logoFailed, setLogoFailed] = useState(false)
  const historyKey = useMemo(() => row ? buildHistoryKey(row) : '', [row])

  useEffect(() => {
    if (!row) return undefined
    let cancelled = false
    setLoading(true)
    setProvenance(null)
    setHistory(null)
    setProvenanceError(null)
    setHistoryError(null)
    setLogoFailed(false)
    const provenanceTask = row.pid == null
      ? Promise.reject(new Error('row has no provenance index (pid)'))
      : fetchLiveOddsProvenance(categorySlug, row.pid, { expectedRow: row })
    const historyTask = fetchLiveOddsHistory({ key: historyKey, historyKey, row, categorySlug })
    Promise.allSettled([provenanceTask, historyTask]).then(([provenanceResult, historyResult]) => {
      if (cancelled) return
      if (provenanceResult.status === 'fulfilled') setProvenance(provenanceResult.value)
      else setProvenanceError(String(provenanceResult.reason?.message || provenanceResult.reason))
      if (historyResult.status === 'fulfilled') setHistory(historyResult.value)
      else setHistoryError(String(historyResult.reason?.message || historyResult.reason))
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [row, categorySlug, historyKey])

  useEffect(() => {
    if (!row) return undefined
    const closeOnEscape = (event) => { if (event.key === 'Escape') onClose() }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [row, onClose])

  if (!row) return null
  const identity = resolveBookIdentity(row.book)
  const deepLink = buildOfferDeepLink(row)
  const proofLink = proofPageForBook(row.book)
  const provenanceAudit = provenance?.audit || auditProvenanceRecord(provenance?.record, row)
  const rawPath = provenanceAudit.matches && (provenance?.record?.raw_path || provenance?.record?.path || provenance?.record?.source_path
    || provenance?.raw_path || provenance?.path || provenance?.source_path || null
  )

  return (
    <div className="lovx-drawer-layer" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}>
      <aside className="lovx-drawer" role="dialog" aria-modal="true" aria-label="Raw offer inspection">
        <header className="lovx-drawer-header">
          <div className="lovx-drawer-book">
            {identity.logoUrl && !logoFailed ? <img src={identity.logoUrl} alt="" onError={() => setLogoFailed(true)} /> : <span>{identity.displayName.slice(0, 2)}</span>}
            <div><small>Raw offer inspection</small><h3>{identity.displayName}</h3></div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </header>
        <section className="lovx-drawer-quote">
          <div><small>Event</small><strong>{row.event || 'Unknown event'}</strong><span>{row.league || '—'} · {row.et_date || 'date unknown'} · {row.et_time || 'time unknown'}</span></div>
          <div><small>{row.bet_type || 'Unknown market'} · {row.period || 'Game'}</small><strong>{row.selection || 'Unknown'} {row.line == null ? '' : row.line}</strong><b>{formatOdds(row.american, oddsFormat)}</b></div>
        </section>
        <nav className="lovx-evidence-links">
          <a href={proofLink} target="_blank" rel="noreferrer">Source proof <ExternalLink size={12} /></a>
          {deepLink ? <a href={deepLink} target="_blank" rel="noreferrer">Open offer / betslip <ExternalLink size={12} /></a> : <span>No supported betslip deep link</span>}
        </nav>
        <section className="lovx-history-section">
          <div className="lovx-section-title"><div><small>On-demand artifact</small><h4>Price history</h4></div><code title={historyKey}>{historyKey}</code></div>
          <EvidenceState loading={loading} error={historyError} emptyLabel="No stored samples for this exact offer.">
            <LiveOddsVNextHistoryChart payload={history} historyKey={historyKey} />
          </EvidenceState>
        </section>
        <section className="lovx-provenance-section">
          <div className="lovx-section-title"><div><small>On-demand artifact</small><h4>Provenance</h4></div>{rawPath && <code>{rawPath}</code>}</div>
          <EvidenceState loading={loading} error={provenanceError} emptyLabel="No provenance row is available for this pid.">
            {provenance && (provenanceAudit.matches
              ? <><div className="lovx-evidence-state">{provenance.resolution === 'identity_recovered' ? `Stale pid ${provenance.requestedPid} recovered by unique offer identity at provenance index ${provenance.resolvedPid}.` : `Provenance index ${provenance.resolvedPid} verified.`}</div><JsonBlock label="Source provenance" value={provenance} /></>
              : <><div className="lovx-evidence-state warn">Provenance {provenance.resolution}: pid {row.pid ?? '—'} cannot be certified for this raw row{provenanceAudit.mismatchFields.length ? ` (${provenanceAudit.mismatchFields.join(', ')})` : ''}. It is diagnostic only, not proof.</div><JsonBlock label="Unverified provenance record (diagnostic only)" value={provenance} /></>)}
          </EvidenceState>
          <JsonBlock label="Raw offer row" value={row} />
        </section>
      </aside>
    </div>
  )
}
